// Malaysian formatting helpers, matching the admin portal + design mockup.
export const RM = (n) =>
  'RM ' + Number(n || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Exact ringgit with the trailing ".00" dropped — cents render only when they are
 * real. For money the agent is owed and might quote back to HQ (deal rows, override
 * rows), where rounding would be wrong but ".00" on every line is just noise.
 */
export const RM0 = (n) => {
  const v = Number(n) || 0;
  const d = Math.round(v * 100) % 100 === 0 ? 0 : 2;
  return 'RM ' + v.toLocaleString('en-MY', { minimumFractionDigits: d, maximumFractionDigits: d });
};

/**
 * Compact ringgit for figures that are CONTEXT rather than a claim: the GCI hero,
 * the period tiles, tier thresholds. Three significant digits and a suffix, so the
 * string is width-bounded — 124px at 24px/700 at any magnitude up to a billion —
 * and two of them always fit side by side, even on a 360px phone.
 *
 * This used to divide by a thousand and stop there, so anything past a million read
 * "RM 12345.7k" on the Home hero and the Earnings tiles. Exact figures belong in
 * RM() and RM0(), never here.
 */
export const RMk = (n) => {
  const v = Number(n) || 0;
  const a = Math.abs(v);
  const scaled = (x, suffix) => {
    const d = Math.abs(x) < 10 ? 2 : 1; // RM 1.25M · RM 12.3M · RM 123.5M
    return 'RM ' + Number(x.toFixed(d)).toLocaleString('en-MY', { maximumFractionDigits: d }) + suffix;
  };
  if (a >= 1e9) return scaled(v / 1e9, 'B');
  if (a >= 1e6) return scaled(v / 1e6, 'M');
  if (a >= 1e3) return scaled(v / 1e3, 'k');
  return RM0(v);
};

/**
 * RMk's units, but pinned to the scale of `scaleOf` instead of chosen per value.
 *
 * For a figure being ANIMATED toward `scaleOf`: RMk picks its unit from whatever
 * it is handed, so counting up to RM 12.4k would render "RM 940", then
 * "RM 1.20k", then "RM 12.4k" — changing the unit and the string width twice on
 * the way up. Pinning the unit to the destination keeps the number growing and
 * the layout still.
 *
 * Only for values in flight. A settled figure should use RMk, which drops
 * trailing zeros ("RM 1.5M", not "RM 1.50M") — this one keeps them, because a
 * digit appearing and disappearing mid-count is the jitter it exists to prevent.
 */
export const RMkAt = (v, scaleOf) => {
  const a = Math.abs(Number(scaleOf) || 0);
  const unit = (div, suffix) => {
    const d = Math.abs(Number(scaleOf) / div) < 10 ? 2 : 1;
    const n = (Number(v) || 0) / div;
    return `RM ${n.toLocaleString('en-MY', { minimumFractionDigits: d, maximumFractionDigits: d })}${suffix}`;
  };
  if (a >= 1e9) return unit(1e9, 'B');
  if (a >= 1e6) return unit(1e6, 'M');
  if (a >= 1e3) return unit(1e3, 'k');
  return RM0(Math.round(Number(v) || 0));
};

/**
 * "No. 8, Jalan Maarof, Bangsar, 59100 Kuala Lumpur" → "Jalan Maarof, Bangsar".
 *
 * The unit/house number and the postcode-state tail identify a property to nobody;
 * the street and its area do. Trimming to those keeps a list row one line high —
 * the full address is on the sale page. CSS ellipsis is still the backstop.
 */
export const shortAddress = (a) => {
  const parts = String(a || '').split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 2) return parts.join(', ');
  const leadIsNumber = /^(no\.?|lot|unit|blok|block)\b/i.test(parts[0]) || /^[a-z]?[-\d]+$/i.test(parts[0]);
  return (leadIsNumber ? parts.slice(1) : parts).slice(0, 2).join(', ');
};

export const dt = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v); // already a display string (e.g. "2h ago")
  return d.toLocaleDateString('en-MY', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const initials = (name) =>
  (name || '')
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .slice(0, 2)
    .toUpperCase();

// A registration number as it should read. Most are stored whole ("REN52479",
// "PEA4084"); some migrated ones are bare digits, which get the REN they lost.
export const renLabel = (ren) => {
  const v = String(ren || '').trim();
  if (!v) return '';
  return /^\d/.test(v) ? `REN ${v}` : v;
};

// A library document's format, read off its file name ("PDF", "DOCX", …).
// Only a PDF opens in the in-app viewer; anything else is download-only.
export const fileFormat = (d) => (String(d?.file_name || '').match(/\.([a-z0-9]+)$/i)?.[1] || 'pdf').toUpperCase();
export const isPdfFile = (d) => fileFormat(d) === 'PDF';
